import { useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Mail,
  Lock,
  LogIn,
  Sparkles,
  Radar,
  Brain,
  ShieldCheck,
  ArrowRight,
  ChevronLeft,
} from 'lucide-react';

const PILLARS = [
  {
    icon: Brain,
    title: 'Copiloto Gemini',
    desc: 'Inteligência artificial conectada aos dados da sua campanha em tempo real.',
  },
  {
    icon: Radar,
    title: 'Cobertura territorial viva',
    desc: 'Mapas, lideranças e movimentações de campo num único radar.',
  },
  {
    icon: ShieldCheck,
    title: 'LGPD por design',
    desc: 'Multi-tenant com RLS, audit log imutável e direitos do titular embarcados.',
  },
];

export function LoginScreen() {
  const { signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [mode, setMode] = useState<'oauth' | 'credentials'>('oauth');

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos.');
      return;
    }
    if (!turnstileToken) {
      toast.error('Por favor, complete a validação anti-robô.');
      return;
    }

    setLoading(true);
    try {
      const verifyRes = await supabase.functions.invoke('verify-turnstile', {
        body: { token: turnstileToken },
      });

      if (verifyRes.error) {
        throw new Error('Falha na validação de segurança anti-robô.');
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      toast.success('Login realizado com sucesso!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'E-mail ou senha inválidos.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha no login com Google.';
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-midnight text-ink lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* ============================================
       *  LADO ESQUERDO — Cinematográfico (desktop only)
       * ============================================ */}
      <aside className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16 overflow-hidden noise">
        {/* Mesh + aurora orbs */}
        <div className="absolute inset-0 -z-10 mesh-midnight">
          <div
            className="aurora-orb aurora-orb-accent animate-aurora-1"
            style={{ width: 520, height: 520, top: '-10%', left: '-10%' }}
          />
          <div
            className="aurora-orb aurora-orb-violet animate-aurora-2"
            style={{ width: 460, height: 460, bottom: '-8%', right: '-8%' }}
          />
          <div
            className="aurora-orb aurora-orb-primary animate-aurora-3"
            style={{ width: 380, height: 380, top: '38%', left: '32%', opacity: 0.4 }}
          />
        </div>

        {/* Grid luminosa decorativa */}
        <div className="absolute inset-0 -z-10 grid-glow opacity-30" />

        {/* Marca topo */}
        <header className="relative z-10 flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-none">
            <span className="font-display text-base font-extrabold tracking-tight text-white">
              Lumenise Base
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-subtle mt-1">
              Plataforma 2026
            </span>
          </div>
        </header>

        {/* Headline + pilares */}
        <div className="relative z-10 max-w-xl space-y-10 animate-fade-in-slow">
          <div className="space-y-5">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-accent glass">
              <Sparkles className="h-3 w-3" />
              Inteligência política contínua
            </span>
            <h1 className="font-display text-5xl xl:text-6xl font-extrabold leading-[0.95] tracking-tight">
              <span className="block text-white">Comande sua base</span>
              <span className="block text-gradient-cinematic">com clareza absoluta.</span>
            </h1>
            <p className="max-w-md text-base text-ink-muted leading-relaxed">
              Coordene lideranças, monitore territórios e converse com seus dados — orquestrado por
              IA, do mandato à urna.
            </p>
          </div>

          <ul className="space-y-4">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.title}
                  className="flex items-start gap-4 animate-fade-in"
                  style={{ animationDelay: `${300 + i * 120}ms` }}
                >
                  <div className="relative shrink-0">
                    <div className="absolute inset-0 rounded-xl blur-md bg-accent/30" />
                    <div className="relative h-10 w-10 rounded-xl glass flex items-center justify-center">
                      <Icon className="h-4.5 w-4.5 text-accent" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{p.title}</p>
                    <p className="text-xs text-ink-muted leading-relaxed mt-0.5 max-w-sm">
                      {p.desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <footer className="relative z-10 flex items-center justify-between text-[11px] text-ink-subtle">
          <span className="font-mono">© 2026 Lumenise</span>
          <div className="flex items-center gap-4">
            <span className="font-mono uppercase tracking-wider">White-label</span>
            <span className="h-1 w-1 rounded-full bg-ink-subtle/60" />
            <span className="font-mono uppercase tracking-wider">Multi-tenant</span>
            <span className="h-1 w-1 rounded-full bg-ink-subtle/60" />
            <span className="font-mono uppercase tracking-wider">LGPD</span>
          </div>
        </footer>
      </aside>

      {/* ============================================
       *  LADO DIREITO — Formulário
       * ============================================ */}
      <section className="relative flex min-h-screen items-center justify-center px-6 py-12 lg:py-16">
        {/* Mobile mesh sutil */}
        <div className="absolute inset-0 -z-10 lg:hidden mesh-midnight">
          <div
            className="aurora-orb aurora-orb-accent animate-aurora-1"
            style={{ width: 360, height: 360, top: '-15%', right: '-20%' }}
          />
          <div
            className="aurora-orb aurora-orb-violet animate-aurora-2"
            style={{ width: 300, height: 300, bottom: '-10%', left: '-15%' }}
          />
        </div>

        {/* Glow vertical separador */}
        <div className="absolute inset-y-0 left-0 hidden lg:block w-px bg-gradient-to-b from-transparent via-hairline-strong to-transparent" />

        <div className="relative z-10 w-full max-w-md animate-fade-in">
          {/* Brand mobile */}
          <div className="lg:hidden mb-10 flex flex-col items-center gap-3">
            <BrandMark />
            <span className="font-display text-lg font-extrabold tracking-tight text-white">
              Lumenise Base
            </span>
          </div>

          {/* Card de login */}
          <div className="glass-strong rounded-2xl p-8 shadow-deep space-y-7">
            <div className="space-y-1.5">
              <h2 className="font-display text-2xl xl:text-3xl font-bold tracking-tight text-white">
                {mode === 'oauth' ? 'Bem-vindo de volta' : 'Entrar com credenciais'}
              </h2>
              <p className="text-sm text-ink-muted">
                {mode === 'oauth'
                  ? 'Acesse seu painel administrativo unificado.'
                  : 'Use seu e-mail e senha para acessar.'}
              </p>
            </div>

            {mode === 'oauth' ? (
              <div className="space-y-5">
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-3 rounded-xl px-4 py-3.5 text-sm font-semibold transition-all btn-ghost hover:text-white cursor-pointer"
                >
                  <GoogleIcon className="h-5 w-5" />
                  <span>Entrar com Google</span>
                  <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 transition-all duration-base group-hover:opacity-100 group-hover:translate-x-0" />
                </button>

                <div className="flex items-center gap-4">
                  <div className="flex-grow divider-fade" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                    ou
                  </span>
                  <div className="flex-grow divider-fade" />
                </div>

                <button
                  onClick={() => setMode('credentials')}
                  className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-ink-muted hover:text-accent transition-colors cursor-pointer"
                >
                  <Mail className="h-4 w-4" />
                  <span>Entrar com e-mail e senha</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-5">
                <PremiumField
                  id="email"
                  label="E-mail"
                  type="email"
                  icon={Mail}
                  placeholder="seu-email@exemplo.com"
                  value={email}
                  onChange={(v) => setEmail(v)}
                  required
                />

                <PremiumField
                  id="password"
                  label="Senha"
                  type="password"
                  icon={Lock}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(v) => setPassword(v)}
                  required
                  trailing={
                    <a
                      href="#forgot"
                      className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle hover:text-accent"
                    >
                      Esqueci
                    </a>
                  }
                />

                <div className="flex justify-center pt-1">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                    onSuccess={(token) => setTurnstileToken(token)}
                    options={{
                      theme: 'dark',
                      language: 'pt-br',
                      size: 'flexible',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 text-sm btn-accent cursor-pointer"
                >
                  {loading ? (
                    <Spinner />
                  ) : (
                    <>
                      <LogIn className="h-4 w-4" />
                      <span>Entrar na conta</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setMode('oauth')}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-ink-subtle hover:text-ink transition-colors pt-1 cursor-pointer"
                >
                  <ChevronLeft className="h-3 w-3" />
                  <span>Voltar ao login com Google</span>
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-[11px] text-ink-subtle mt-6 leading-relaxed">
            Ao entrar, você concorda com nossos{' '}
            <a href="/legal/termos" className="text-accent font-semibold hover:underline">
              Termos
            </a>{' '}
            e{' '}
            <a href="/legal/privacidade" className="text-accent font-semibold hover:underline">
              Política de Privacidade
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}

/* ============================================================
 * Componentes auxiliares
 * ============================================================ */

function BrandMark() {
  return (
    <div className="relative h-10 w-10 shrink-0">
      <div className="absolute inset-0 rounded-xl blur-lg bg-accent/40" />
      <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-accent to-accent/60 flex items-center justify-center shadow-glow-sm">
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-midnight" fill="currentColor">
          <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.5L17.5 8 12 11.5 6.5 8 12 4.5zM6 9.5l5 3v8.25l-5-3.125V9.5zm12 0v8.125l-5 3.125V12.5l5-3z" />
        </svg>
      </div>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

interface PremiumFieldProps {
  id: string;
  label: string;
  type: string;
  icon: typeof Mail;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  trailing?: React.ReactNode;
}

function PremiumField({
  id,
  label,
  type,
  icon: Icon,
  placeholder,
  value,
  onChange,
  required,
  trailing,
}: PremiumFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <label
          htmlFor={id}
          className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-muted"
        >
          {label}
        </label>
        {trailing}
      </div>
      <div className="group relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle group-focus-within:text-accent transition-colors" />
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-midnight-2/60 hairline text-ink placeholder-ink-faint focus:outline-none focus:border-accent/40 focus:bg-midnight-2 focus:shadow-glow-xs transition-all text-sm"
        />
      </div>
    </div>
  );
}
